<script setup lang="ts">
import { useProjectDetail } from '@/composables/useProjectDetail';
import { headerVideo } from '@/data/header';
import {
  HOME_ROUTE_NAME,
  MANUFACTURING_PROCESS_ROUTE,
  MANUFACTURING_PROCESS_ROUTE_NAME,
  RENEWABLE_ENERGY_ROUTE_NAME,
  TECH_MEASURE_DETAIL_ROUTES_NAMES,
  TECH_MEASURES_WITH_TYPE_ROUTE_NAME
} from '@/data/routes';
import {
  defaultMeasureImage,
  manufacturingSelectionTabs,
  techAndMeasureTabs
} from '@/data/techAndMeasures/index';
import { getApiImageURL } from '@/services/images';
import { useTechMeasuresStore } from '@/store/techMeasures';
import type { TabItem } from '@/types';
import { useEventListener } from '@vueuse/core';
import { computed, ref } from 'vue';
import NumberAnimation from 'vue-number-animation';
import { useRoute, useRouter } from 'vue-router';
import { filterProjectsBySelectedTab } from '@/services/tech-measures-service';
import { t } from '@/locales/i18n';

interface IHeaderComponent {
  measure?: string;
  type?: string;
  manufacturing?: string;
}

const props = withDefaults(defineProps<IHeaderComponent>(), {
  measure: '',
  type: '',
  manufacturing: ''
});

const currentVideo = getApiImageURL(headerVideo);
const SM = 768;
const SM_TAB_HEIGHT = 70;

const projectDetail = useProjectDetail(props);

const route = useRoute();

const router = useRouter();

const techMeasureStore = useTechMeasuresStore();

const headerTabs = ref<HTMLElement | null>(null);

const isScrolledOver = ref(false);

const isScrolledOverTabs = ref(false);

const selectedManufacturingOption = ref('');

useEventListener(
  document,
  'scroll',
  () => {
    if (window.innerWidth <= SM) {
      calculateOverflowSmallSizes();
    } else {
      calculateOverflow();
    }
  },
  true
);

const currentManufacturingOption = computed(() =>
  selectedManufacturingOption.value.length > 0
    ? selectedManufacturingOption.value
    : props.manufacturing
);

const hasManufacturingSelectionTabs = computed(
  () => isManufacturingSelectionPage.value && !isTextDisplayed.value
);

const hasTechMeasuresTabs = computed(
  () =>
    !isHomePage.value &&
    !isManufacturingSelectionPage.value &&
    !!techMeasureTabs.value.length &&
    !isScrolledOverTabs.value
);

const headerStyles = computed(() => ({
  'header-component': true,
  'header-component--full': isFullScreen.value
}));

const isFullScreen = computed(() => isHomePage.value || isManufacturingSelectionPage.value);

const isGoBackDisplayed = computed(() => techMeasureTabs.value.length === 1);

const isHomePage = computed(() => route.name === HOME_ROUTE_NAME);

const isManufacturingSelectionPage = computed(
  () => route.name === MANUFACTURING_PROCESS_ROUTE_NAME
);

const headerImage = computed(() =>
  getApiImageURL(projectDetail.projectData.value?.image ?? defaultMeasureImage)
);

const isMeasureDetailPage = computed(
  () => TECH_MEASURE_DETAIL_ROUTES_NAMES.indexOf(route.name as string) > -1
);

const isTextDisplayed = computed(() => isHomePage.value && !isScrolledOver.value);

const techMeasureTabs = computed(() =>
  props.type.length > 0
    ? techAndMeasureTabs.value.filter((tab) => filterProjectsBySelectedTab(props.type, tab.type))
    : route?.name === RENEWABLE_ENERGY_ROUTE_NAME
      ? []
      : techAndMeasureTabs.value
);

const calculateOverflow = () => {
  const appBar = document.getElementsByClassName('dp-app-bar');
  if (appBar) {
    isScrolledOver.value = window.scrollY > 0;

    isScrolledOverTabs.value = !!(
      headerTabs.value && appBar[0]!.clientHeight >= headerTabs.value.getBoundingClientRect().top
    );
  }
};

const calculateOverflowSmallSizes = () => {
  isScrolledOverTabs.value = window.scrollY >= SM_TAB_HEIGHT;

  isScrolledOver.value = window.scrollY > 0;
};

const formatIntegers = (num: number) => String(Math.round(num));

const onAllTabs = () => navigateToList();

const navigateToContent = () => router.push(MANUFACTURING_PROCESS_ROUTE);

const navigateToList = () =>
  router.push({
    name: TECH_MEASURES_WITH_TYPE_ROUTE_NAME,
    params: { manufacturing: currentManufacturingOption.value }
  });

const selectedManufacturing = (tab: TabItem) => {
  techMeasureStore.setManufacturingProcess(tab.name);
  selectedManufacturingOption.value = tab.name;

  navigateToList();
};

const selectMeasuresTab = (tab: TabItem) =>
  router.push({
    name: TECH_MEASURES_WITH_TYPE_ROUTE_NAME,
    params: { type: tab.type, manufacturing: currentManufacturingOption.value }
  });

const tabAmount = (tab: TabItem) =>
  techMeasureStore.tabAmounts.filter((tabAmount) => tabAmount.type === tab.type)[0].amount;
</script>

<template>
  <div :class="headerStyles">
    <div class="header-component__content">
      <Transition name="text-fade">
        <div v-if="isTextDisplayed" class="header-component__content-div">
          <p class="header-component__content-text">
            {{ t('home.headerDescription') }}
          </p>

          <DcBtn
            class="header-component__content-btn"
            border-color="white"
            @click="navigateToContent"
            >{{ t('home.headerButton') }}</DcBtn
          >
        </div>
      </Transition>

      <div class="header-component__manufacturing-content">
        <Transition name="manufacturing-title">
          <div v-if="hasManufacturingSelectionTabs" class="header-component__manufacturing-content">
            <p class="header-component__manufacturing-title">
              {{ t('home.manufacturingProcessTitle') }}
            </p>
          </div>
        </Transition>

        <Transition name="selection-transition">
          <div v-if="hasManufacturingSelectionTabs" class="header-component__content-tabs--column">
            <div
              v-for="tab in manufacturingSelectionTabs"
              :key="tab.name"
              class="header-component__manufacturing-tab"
              @click="selectedManufacturing(tab)"
            >
              <div class="header-component__tab-text">{{ tab.name }}</div>
            </div>
          </div>
        </Transition>
      </div>

      <Transition name="measure-tabs-appear">
        <div v-if="hasTechMeasuresTabs" class="header-component__tabs-container" ref="headerTabs">
          <p class="header-component__tabs-container-manufacturing" @click="navigateToContent">
            {{ currentManufacturingOption }}
          </p>
          <div class="header-component__tabs-container-data">
            <Transition name="arrow-fade">
              <IconChevronLeft
                v-if="isGoBackDisplayed"
                size="50"
                @click="onAllTabs"
              ></IconChevronLeft>
            </Transition>

            <TransitionGroup
              name="tabs-transition"
              class="header-component__content-tabs"
              tag="div"
            >
              <div
                v-for="tab in techMeasureTabs"
                :key="tab.name"
                class="header-component__custom-tab"
                @click="selectMeasuresTab(tab)"
              >
                <div class="header-component__tab-text">{{ tab.name }}</div>
                <NumberAnimation
                  autoplay
                  class="header-component__tab-amount"
                  easing="easeOutExpo"
                  :duration="1"
                  :format="formatIntegers"
                  :from="tabAmount(tab)"
                  :to="tabAmount(tab)"
                ></NumberAnimation>
              </div>
            </TransitionGroup>
          </div>
        </div>
      </Transition>
    </div>

    <div class="header-component__media-container">
      <img
        v-if="isMeasureDetailPage"
        alt="Measure background image"
        class="header-component__media"
        id="headerMedia"
        :src="headerImage"
      />

      <video
        v-else
        alt="Header video"
        class="header-component__media"
        id="headerMedia"
        loop
        muted
        autoplay
        :src="currentVideo"
      ></video>

      <div class="header-component__media-overlay"></div>
    </div>
  </div>
</template>

<style lang="scss" src="./HeaderComponent.scss" scoped></style>
